import type { ActionsNetwork } from 'shared/store/actions'
import { fetchPageData } from 'shared/store/actions'
import endpoints from 'shared/store/endpoints'
import { ws } from 'shared/store/ws'
import type { MetaShortDTO, NewPasswordForm } from 'shared/types/api'
import type { Dispatch } from 'redux'
import type { RootState } from 'shared/store/reducers'
import { errors } from 'shared/utils'

type GetState = () => RootState

type ActionResetPasswordPageDataResponse = {
    type: 'PAGE_DATA_RESET_PASSWORD_RESPONSE',
    data: {
        metaData: MetaShortDTO
    }
}

type ActionResetPasswordToken = {
    type: 'RESET_PASSWORD_TOKEN',
    token: string
}

type ActionResetPasswordUpdateError = {
    type: 'RESET_PASSWORD_UPDATE_ERROR',
    resetPasswordUpdateError: any
}

type ActionResetPasswordUpdateSuccess = {
    type: 'RESET_PASSWORD_UPDATE_SUCCESS'
}

type ActionResetPassword = {
    type: 'RESET_PASSWORD'
}

type ActionResetPasswordMailError = {
    type: 'RESET_PASSWORD_SEND_MAIL_ERROR',
    resetPasswordMailError: any
}

type ActionResetPasswordMail = {
    type: 'RESET_PASSWORD_SEND_MAIL',
    email: string
}

type ActionResetEmail = {
    type: 'RESET_EMAIL'
}

const resetEmail = (): ActionResetEmail => ({
    type: 'RESET_EMAIL'
})

const getUrlParams = () => {
    return (dispatch: Dispatch<any>, getState: GetState) => {
        const search = getState().router.location.search
        const token = search.split('token=')[1]
        dispatch(fetchResetPasswordPageData(token))
    }
}

const fetchResetPasswordPageData = (token: string) => {
    return (dispatch: Dispatch<any>) => {
        if (token) {
            dispatch(actionResetPasswordToken(token))
        } else {
            dispatch(
                fetchPageData(
                    endpoints.kingdom.password.reset,
                    'PAGE_DATA_RESET_PASSWORD_RESPONSE'
                )
            )
        }
    }
}

const actionResetPasswordToken = (token: string): ActionResetPasswordToken => ({
    type: 'RESET_PASSWORD_TOKEN',
    token
})

const sendResetPasswordMail = (email: string, queen: string) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            await ws.get(endpoints.kingdom.password.mail, {
                params: { email: email, queen: queen }
            })
            dispatch(actionResetPasswordMail(email))
        } catch (error) {
            const response = error.response
            console.error('Error reset password', response)
            if (
                response &&
                response.data &&
                response.data.message === errors.NOT_FOUND
            ) {
                dispatch(actionResetPasswordMail(email))
            } else {
                dispatch(actionResetPasswordMailError('form.error.default'))
            }
        }
    }
}

const actionResetPasswordMail = (email: string): ActionResetPasswordMail => ({
    type: 'RESET_PASSWORD_SEND_MAIL',
    email
})

const actionResetPasswordMailError = (
    resetPasswordMailError: any
): ActionResetPasswordMailError => ({
    type: 'RESET_PASSWORD_SEND_MAIL_ERROR',
    resetPasswordMailError
})

const resetPasswordUpdate = (form: NewPasswordForm) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        try {
            const { token } = getState().resetPasswordPageState
            await ws.post(endpoints.kingdom.password.reset, {
                ...form,
                token: token
            })
            dispatch(actionResetPasswordUpdateSuccess())
        } catch (error) {
            const { response } = error
            console.error('Error in reset password', response)
            let message = 'form.error.default'
            if (response) {
                const { data } = response
                if (data) {
                    switch (data.message) {
                        case errors.INVALID_TOKEN:
                            message = 'reset.password.update.submit.token.error'
                            break
                        case errors.PASSWORDS_ARE_DIFFERENT:
                            message =
                                'reset.password.update.submit.password.error'
                            break
                        default:
                            break
                    }
                }
            }
            dispatch(actionResetPasswordUpdateError(message))
        }
    }
}

const actionResetPasswordUpdateSuccess = (): ActionResetPasswordUpdateSuccess => ({
    type: 'RESET_PASSWORD_UPDATE_SUCCESS'
})

const resetPassword = (): ActionResetPassword => ({
    type: 'RESET_PASSWORD'
})

const actionResetPasswordUpdateError = (
    resetPasswordUpdateError: any
): ActionResetPasswordUpdateError => ({
    type: 'RESET_PASSWORD_UPDATE_ERROR',
    resetPasswordUpdateError
})

export {
    fetchResetPasswordPageData,
    getUrlParams,
    resetPasswordUpdate,
    resetPassword,
    sendResetPasswordMail,
    resetEmail
}

export type Action =
    | ActionResetPasswordPageDataResponse
    | ActionResetPasswordToken
    | ActionResetPasswordUpdateError
    | ActionResetPasswordUpdateSuccess
    | ActionResetPasswordMail
    | ActionResetPasswordMailError
    | ActionResetPassword
    | ActionResetEmail
    | ActionsNetwork
